import { DIRS, hexDist, hexLen, hexToWorld, type Hex } from '../hex.ts';
import { idiv, imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng, type RngState } from '../math/rng.ts';

/**
 * The autumn wood over Los Hornos (docs/sendas/druida.md, chapter 3): the grounds, the same from the seed in the sim
 * and the drawing, integers only. Up is −y (the top of the screen: up the wood, toward Olmo's maple).
 *
 * - `leafLayout` (act 1, under the leaves): the red wood, Ceda's way winding up it to the maple at the top, the trees
 *   (cells nobody walks) off the way, the spots at the wood's sides where the stalkers and the weavers come in.
 * - `pastLayout` (act 2, the memory, a hundred years back): the young maple at the top; three of its roots come down
 *   the slope toward the six houses of Los Hornos at the foot, each forking once or twice (a tree of cells, each with
 *   the one above it and the ones below); the rot comes down them.
 * - `underLayout` (act 3, the test, «Lo que hay abajo»): the clearing round the maple; the knot where the rot gets in,
 *   and four rotten roots from it to the maple's foot (two show for a stretch, two run under the ground all the way);
 *   the roots that hold the maple, out from its foot (in sight a stretch with Olmo's notch, under after). Each cell
 *   shows its mushrooms: one per root under it, black if a rotten one runs there. A net is kept only if each rotten
 *   root can be cut safely somewhere (a cell where it runs alone) the screen alone tells, few such cells on the ones
 *   under the ground, and a cut on a stretch in sight cuts a sound root under it somewhere.
 */

export const LEAF_RADIUS = 10;
export const PAST_RADIUS = 10;
export const ROOTS_RADIUS = 8;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;
const key = (h: Hex): string => `${h.q},${h.r}`;

/** The squared distance from a point to a segment (sim units). */
const segD2 = (x: number, y: number, ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const L2 = dx * dx + dy * dy;
  const k = L2 <= 0 ? 0 : imax(0, imin(1024, idiv(((x - ax) * dx + (y - ay) * dy) * 1024, L2)));
  const px = ax + idiv(dx * k, 1024);
  const py = ay + idiv(dy * k, 1024);
  return (x - px) * (x - px) + (y - py) * (y - py);
};

/* ---------- act 1: under the leaves ---------- */

export interface LeafLayout {
  radius: number;
  rocks: Hex[];
  /** The wood's trees on the board (each a cell nobody walks), the maple's trunk. */
  trees: Hex[];
  maple: Hex;
  /** Ceda's way up (sim points): from where she starts to the maple's foot. */
  way: { x: number; y: number }[];
  ceda: { x: number; y: number };
  start: { x: number; y: number };
  /** Where the stalkers and the weavers come in from (the wood's sides, sim points). */
  rim: { x: number; y: number }[];
}

export const leafLayout = (seed: number): LeafLayout => {
  const R = LEAF_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x1eaf));
  const maple = { q: 4, r: -8 };
  const m = hexToWorld(maple.q, maple.r);
  const base = hexToWorld(-4, 8);
  // The way: five legs up the wood, swinging from side to side, the last to the maple's foot.
  const way: { x: number; y: number }[] = [{ x: base.x, y: base.y - 400 }];
  const side = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let i = 1; i <= 4; i++) {
    const y = base.y - 400 - idiv((base.y - 400 - (m.y + 1900)) * i, 5);
    const x = (i % 2 === 0 ? -side : side) * rngRange(rng, 1800, 3800);
    way.push({ x, y });
  }
  way.push({ x: m.x, y: m.y + 1900 });
  const near = (c: { x: number; y: number }, d: number): boolean => {
    for (let i = 1; i < way.length; i++) {
      const a = way[i - 1] as { x: number; y: number };
      const b = way[i] as { x: number; y: number };
      if (segD2(c.x, c.y, a.x, a.y, b.x, b.y) < d * d) return true;
    }
    return false;
  };
  // The trees: off the way (a hex and a half), not by the maple nor by the foot where they start.
  const trees: Hex[] = [];
  const cand = allCells(R - 1).filter((h) => {
    const c = hexToWorld(h.q, h.r);
    return !near(c, 2000) && hexDist(h, maple) > 2 && hexDist(h, { q: -4, r: 8 }) > 2;
  });
  for (let guard = 0; trees.length < 26 && guard < 900; guard++) {
    const h = cand[rngInt(rng, cand.length)] as Hex;
    if (trees.some((o) => hexDist(o, h) < 2)) continue;
    trees.push(h);
  }
  trees.sort(sortHex);
  const rocks = [...trees, maple].sort(sortHex);
  const rim: { x: number; y: number }[] = [];
  for (let k = 0; k < 8; k++) {
    const y = (-6 + k * 2) * 866;
    rim.push({ x: (k % 2 === 0 ? -1 : 1) * (7800 - Math.abs(k - 4) * 350), y });
  }
  return { radius: R, rocks, trees, maple, way, ceda: { ...(way[0] as { x: number; y: number }) }, start: { x: base.x + 1100, y: base.y - 200 }, rim };
};

/* ---------- act 2: the memory, Olmo's autumn ---------- */

export interface BlightNode {
  q: number;
  r: number;
  /** The node above (−1: the root's top, by the maple), the ones below, which of the three roots. */
  up: number;
  down: number[];
  src: number;
}

export interface PastLayout {
  radius: number;
  rocks: Hex[];
  /** The roots' cells (by order from each top down), the tops (one per root), the ends at the houses. */
  nodes: BlightNode[];
  srcs: number[];
  ends: number[];
  /** The young maple; the houses of Los Hornos (for the drawing) below the ends; the row they stand at. */
  maple: Hex;
  houses: Hex[];
  houseRow: number;
  /** The wood's trees (cells nobody walks, off the roots). */
  trees: Hex[];
  start: { x: number; y: number };
  colliers: { x: number; y: number }[];
  rim: { x: number; y: number }[];
}

const PAST_TOP = -6;
const PAST_END = 6;

const growPast = (rng: RngState): { nodes: BlightNode[]; srcs: number[]; ends: number[] } | null => {
  const nodes: BlightNode[] = [];
  const used = new Set<string>();
  const srcs: number[] = [];
  const centers = [-4800, 0, 4800];
  const tops = [{ q: -2, r: PAST_TOP }, { q: 3, r: PAST_TOP }, { q: 8, r: PAST_TOP }];
  for (let s = 0; s < 3; s++) {
    const top = tops[s] as Hex;
    const cx = centers[s] as number;
    nodes.push({ q: top.q, r: top.r, up: -1, down: [], src: s });
    used.add(key(top));
    srcs.push(nodes.length - 1);
    // Heads going down: the node, its target x, whether it may still fork, its steps to its next fork.
    const heads: { i: number; tx: number; forks: number; left: number }[] = [{ i: nodes.length - 1, tx: cx, forks: 2, left: rngRange(rng, 2, 3) }];
    while (heads.length) {
      const hd = heads.shift() as { i: number; tx: number; forks: number; left: number };
      const n = nodes[hd.i] as BlightNode;
      if (n.r >= PAST_END) continue;
      const dl = { q: n.q - 1, r: n.r + 1 };
      const dr = { q: n.q, r: n.r + 1 };
      const ok = (h: Hex): boolean => {
        const x = hexToWorld(h.q, h.r).x;
        return !used.has(key(h)) && hexLen(h.q, h.r) <= PAST_RADIUS - 1 && x >= cx - 2400 && x <= cx + 2400;
      };
      const add = (h: Hex): number => {
        nodes.push({ q: h.q, r: h.r, up: hd.i, down: [], src: s });
        used.add(key(h));
        n.down.push(nodes.length - 1);
        return nodes.length - 1;
      };
      if (hd.left <= 0 && hd.forks > 0 && ok(dl) && ok(dr)) {
        const spread = hd.forks === 2 ? 1300 : 700;
        const a = add(dl);
        const b = add(dr);
        heads.push({ i: a, tx: hd.tx - spread, forks: hd.forks - 1, left: rngRange(rng, 3, 4) });
        heads.push({ i: b, tx: hd.tx + spread, forks: hd.forks - 1 - (rngInt(rng, 2) === 0 ? 1 : 0), left: rngRange(rng, 3, 4) });
        continue;
      }
      const x = hexToWorld(n.q, n.r).x;
      const first = x > hd.tx + 300 ? dl : x < hd.tx - 300 ? dr : rngInt(rng, 2) === 0 ? dl : dr;
      const other = first === dl ? dr : dl;
      const next = ok(first) ? first : ok(other) ? other : null;
      if (!next) return null;
      heads.push({ i: add(next), tx: hd.tx, forks: hd.forks, left: hd.left - 1 });
    }
  }
  const ends = nodes.map((n, i) => (n.r >= PAST_END ? i : -1)).filter((i) => i >= 0);
  // Every root forks at least once and comes down to the houses.
  for (let s = 0; s < 3; s++) if (ends.filter((i) => (nodes[i] as BlightNode).src === s).length < 2) return null;
  return { nodes, srcs, ends };
};

export const pastLayout = (seed: number): PastLayout => {
  let net: ReturnType<typeof growPast> = null;
  for (let k = 0; !net && k < 200; k++) net = growPast(seedRng(deriveSeed(seed, 0x9a57 + k)));
  if (!net) throw new Error('pastLayout: no net');
  const rng = seedRng(deriveSeed(seed, 0x9a58));
  const maple = { q: 4, r: -8 };
  const houseRow = PAST_END + 1;
  const houses: Hex[] = [];
  for (let q = -9; q <= 9; q++) for (const r of [houseRow, houseRow + 1]) if (hexLen(q, r) <= PAST_RADIUS && (q + r * 3) % 2 === 0) houses.push({ q, r });
  const rootAt = new Set(net.nodes.map((n) => key(n)));
  const trees: Hex[] = [];
  const cand = allCells(PAST_RADIUS - 1).filter((h) => h.r > -8 && h.r < PAST_END && !rootAt.has(key(h)) && !net.nodes.some((n) => hexDist(n, h) < 2) && hexDist(h, maple) > 2);
  for (let guard = 0; trees.length < 14 && guard < 600; guard++) {
    const h = cand[rngInt(rng, cand.length)] as Hex;
    if (trees.some((o) => hexDist(o, h) < 3)) continue;
    trees.push(h);
  }
  trees.sort(sortHex);
  const rocks = [...trees, maple, ...houses].sort(sortHex);
  const colliers = [-4800, 0, 4800].map((x) => ({ x: x + 600, y: PAST_END * 866 + 300 }));
  const rim = [-7600, 7600].flatMap((x) => [-4, 0, 3].map((r) => ({ x, y: r * 866 })));
  return { radius: PAST_RADIUS, rocks, nodes: net.nodes, srcs: net.srcs, ends: net.ends, maple, houses, houseRow, trees, start: { x: 1200, y: 0 }, colliers, rim };
};

/* ---------- act 3: what lies below ---------- */

export interface UnderStrand {
  /** Its cells from its start (the knot's, for a rotten one; the maple's foot, for a sound one) on. */
  cells: Hex[];
  rot: boolean;
  /** Its first cells in sight (a rotten one's from 1, after the knot; a sound one's from 0, with the notch). */
  vis: number;
}

export interface UnderLayout {
  radius: number;
  rocks: Hex[];
  /** The maple's trunk and the flare of its roots (cells nobody walks), its middle; the knot. */
  base: Hex[];
  maple: Hex;
  knot: Hex;
  strands: UnderStrand[];
  /**
   * Every cell with a root (sorted), and for each: the strands on it, the mushrooms (roots under it, out of sight),
   * black (a rotten one among them), what shows on it (0 nothing, 1 a rotten root, 2 a sound one with the notch).
   */
  cells: Hex[];
  on: number[][];
  mush: number[];
  black: number[];
  vis: number[];
  /** Where each strand runs through each cell: `at[strand][k]` the index of its k-th cell in `cells`. */
  at: number[][];
  trees: Hex[];
  start: { x: number; y: number };
  ceda: { x: number; y: number };
  hob: { x: number; y: number };
  rim: { x: number; y: number }[];
}

const UNDER_MAPLE = { q: 3, r: -6 };
const UNDER_KNOT = { q: -2, r: 5 };

const step = (h: Hex, d: Hex): Hex => ({ q: h.q + d.q, r: h.r + d.r });

/** A walk from `from` toward `to` over free cells (null: stuck), wandering a little. */
const walk = (rng: RngState, from: Hex, to: Hex, free: (h: Hex) => boolean, max: number): Hex[] | null => {
  const out: Hex[] = [from];
  let at = from;
  while (hexDist(at, to) > 0) {
    if (out.length > max) return null;
    const d0 = hexDist(at, to);
    const closer: Hex[] = [];
    const level: Hex[] = [];
    for (const d of DIRS) {
      const n = step(at, d);
      if (!free(n) && !(n.q === to.q && n.r === to.r)) continue;
      if (out.some((o) => o.q === n.q && o.r === n.r)) continue;
      const dd = hexDist(n, to);
      if (dd < d0) closer.push(n);
      else if (dd === d0) level.push(n);
    }
    const pick = level.length && (rngInt(rng, 100) < 22 || !closer.length) ? level : closer;
    if (!pick.length) return null;
    at = pick[rngInt(rng, pick.length)] as Hex;
    out.push(at);
  }
  return out;
};

/** Everything a screen shows of a net (for its check, the HUD and a bot that reads only that). */
const underCells = (strands: UnderStrand[]): Pick<UnderLayout, 'cells' | 'on' | 'mush' | 'black' | 'vis' | 'at'> => {
  const idx = new Map<string, number>();
  const all: Hex[] = [];
  for (const st of strands) for (let k = st.rot ? 1 : 0; k < st.cells.length; k++) {
    const h = st.cells[k] as Hex;
    if (!idx.has(key(h))) {
      idx.set(key(h), -1);
      all.push(h);
    }
  }
  all.sort(sortHex);
  all.forEach((h, i) => idx.set(key(h), i));
  const on = all.map((): number[] => []);
  const mush = all.map(() => 0);
  const black = all.map(() => 0);
  const vis = all.map(() => 0);
  const at = strands.map((st) =>
    st.cells.map((h, k) => {
      if (st.rot && k === 0) return -1;
      const i = idx.get(key(h)) as number;
      (on[i] as number[]).push(strands.indexOf(st));
      const seen = st.rot ? k <= st.vis : k < st.vis;
      if (seen) vis[i] = st.rot ? 1 : 2;
      else {
        mush[i] = (mush[i] as number) + 1;
        if (st.rot) black[i] = 1;
      }
      return i;
    }),
  );
  return { cells: all, on, mush, black, vis, at };
};

/**
 * The cells a screen says are safe to cut: a rotten root in sight with nothing under it, or one black mushroom alone,
 * where the rot hasn't come yet (`rotten`: the cells it has, which the ground shows).
 */
export const underSafe = (L: Pick<UnderLayout, 'cells' | 'mush' | 'black' | 'vis'>, rotten: ReadonlySet<number> = new Set()): number[] => {
  const out: number[] = [];
  for (let i = 0; i < L.cells.length; i++) {
    if (rotten.has(i)) continue;
    if ((L.vis[i] === 1 && L.mush[i] === 0) || (L.vis[i] === 0 && L.mush[i] === 1 && L.black[i] === 1)) out.push(i);
  }
  return out;
};

/** The cells the rot has as the fight starts: each rotten root's first `pre` after the knot. */
export const underRotten = (L: Pick<UnderLayout, 'strands' | 'at'>, pre: number): Set<number> => {
  const out = new Set<number>();
  L.strands.forEach((st, k) => {
    if (!st.rot) return;
    for (let j = 1; j <= pre; j++) {
      const i = (L.at[k] as number[])[j];
      if (i !== undefined && i >= 0) out.add(i);
    }
  });
  return out;
};

const tryUnder = (rng: RngState, pre: number): UnderLayout | null => {
  const R = ROOTS_RADIUS;
  const M = UNDER_MAPLE;
  const E = UNDER_KNOT;
  const base = [M, ...DIRS.map((d) => step(M, d))];
  const baseAt = new Set(base.map(key));
  const inside = (h: Hex): boolean => hexLen(h.q, h.r) <= R - 1;
  // The rotten roots: from four of the knot's sides to four cells round the maple's foot, left to right.
  const ring = allCells(R).filter((h) => hexDist(h, M) === 2 && h.r >= M.r - 1 && inside(h));
  ring.sort((a, b) => hexToWorld(a.q, a.r).x - hexToWorld(b.q, b.r).x);
  if (ring.length < 6) return null;
  const starts = [step(E, { q: -1, r: 0 }), step(E, { q: 0, r: -1 }), step(E, { q: 1, r: -1 }), step(E, { q: 1, r: 0 })];
  const spread = ring.length - 1;
  const pickEnds = [0, idiv(spread, 3) + rngInt(rng, 2), idiv(spread * 2, 3) + rngInt(rng, 2) - 1, spread].map((i) => ring[imin(spread, i)] as Hex);
  if (new Set(pickEnds.map(key)).size < 4) return null;
  // (Each root's start kept for it: none walks through another's.)
  const used = new Set<string>([key(E), ...base.map(key), ...starts.map(key)]);
  const strands: UnderStrand[] = [];
  for (let i = 0; i < 4; i++) {
    const s0 = starts[i] as Hex;
    const end = pickEnds[i] as Hex;
    const path = walk(rng, s0, end, (h) => inside(h) && !used.has(key(h)) && !pickEnds.some((p, j) => j !== i && p.q === h.q && p.r === h.r), 18);
    if (!path || path.length < 7) return null;
    for (const h of path) used.add(key(h));
    strands.push({ cells: [E, ...path], rot: true, vis: 0 });
  }
  // Two of them show for a stretch after the knot; two run under the ground all the way.
  const a = rngInt(rng, 4);
  const b = (a + 1 + rngInt(rng, 3)) % 4;
  for (const i of [a, b]) {
    const st = strands[i] as UnderStrand;
    st.vis = rngRange(rng, pre + 2, imax(pre + 2, st.cells.length - 3));
  }
  const rotVisAt = new Set<string>();
  for (const st of strands) st.cells.forEach((h, k) => k >= 1 && k <= st.vis && rotVisAt.add(key(h)));
  const sound: UnderStrand[] = [];
  /** A sound root on from its last cell, outward from the maple over free ground (`like`: cells it would rather take). */
  const onward = (cells: Hex[], len: number, like: Set<string>): void => {
    let at = cells[cells.length - 1] as Hex;
    while (cells.length < len) {
      const d0 = hexDist(at, M);
      const cand: Hex[] = [];
      const w: number[] = [];
      for (const d of DIRS) {
        const n = step(at, d);
        if (!inside(n) || baseAt.has(key(n)) || (n.q === E.q && n.r === E.r) || cells.some((o) => o.q === n.q && o.r === n.r)) continue;
        const dd = hexDist(n, M);
        if (dd < d0) continue;
        cand.push(n);
        w.push(like.has(key(n)) ? 6 : dd > d0 ? 2 : 1);
      }
      if (!cand.length) return;
      let tot = 0;
      for (const x of w) tot += x;
      let pick = rngInt(rng, tot);
      let j = 0;
      while (pick >= (w[j] as number)) pick -= w[j++] as number;
      at = cand[j] as Hex;
      cells.push(at);
    }
  };
  // Along each root under the ground all the way, from the maple's foot back toward the knot: a sound root that runs
  // with it (two mushrooms there, one black) and leaves it one or two cells past where the rot has come, out to the side.
  for (let i = 0; i < 4; i++) {
    const st = strands[i] as UnderStrand;
    if (st.vis) continue;
    const L = st.cells.length;
    const j = rngRange(rng, pre + 1, pre + 2);
    if (L - 1 <= j + 2) return null;
    const cells: Hex[] = [];
    for (let k = L - 1; k > j; k--) cells.push(st.cells[k] as Hex);
    onward(cells, cells.length + rngRange(rng, 2, 3), new Set());
    sound.push({ cells, rot: false, vis: 2 });
  }
  // Two more out from the maple's foot, under the rotten roots in sight here and there (a cut there cuts them too).
  const soundFrom = allCells(R).filter((h) => hexDist(h, M) === 2 && inside(h) && !used.has(key(h)));
  for (let k = 0; k < 2 && soundFrom.length; k++) {
    const s0 = soundFrom.splice(rngInt(rng, soundFrom.length), 1)[0] as Hex;
    const cells: Hex[] = [s0];
    onward(cells, rngRange(rng, 7, 10), rotVisAt);
    if (cells.length < 5) return null;
    // (Its notched stretch never on a rotten root in sight.)
    if (rotVisAt.has(key(cells[0] as Hex)) || rotVisAt.has(key(cells[1] as Hex))) return null;
    sound.push({ cells, rot: false, vis: 2 });
  }
  if (sound.length < 4) return null;
  const all = [...strands, ...sound];
  const view = underCells(all);
  // Three mushrooms at most on a cell (a sound root's notched stretch never lies on a rotten one in sight: kept above).
  for (let i = 0; i < view.cells.length; i++) if ((view.mush[i] as number) > 3) return null;
  const safe = new Set(underSafe(view, underRotten({ strands: all, at: view.at }, pre)));
  let traps = 0;
  for (let s = 0; s < 4; s++) {
    const st = strands[s] as UnderStrand;
    const mine = (view.at[s] as number[]).filter((i) => i >= 0 && safe.has(i));
    if (!mine.length) return null;
    if (st.vis === 0) {
      // Under the ground all the way: one or two cells where it runs alone, two at least where a sound one crosses it.
      if (mine.length > 2) return null;
      const shared = (view.at[s] as number[]).filter((i) => i >= 0 && (view.on[i] as number[]).length > 1).length;
      if (shared < 2) return null;
    } else {
      for (let k = pre + 1; k <= st.vis; k++) {
        const i = (view.at[s] as number[])[k] as number;
        if ((view.mush[i] as number) > 0) traps++;
      }
    }
  }
  if (traps < 1) return null;
  const rootAt = new Set(view.cells.map(key));
  const trees: Hex[] = [];
  for (const h of allCells(R)) {
    if (hexLen(h.q, h.r) < R - 1 || rootAt.has(key(h)) || baseAt.has(key(h))) continue;
    if ((h.q * 7 + h.r * 13 + 400) % 3 === 0) trees.push(h);
  }
  trees.sort(sortHex);
  const rocks = [...base, ...trees].sort(sortHex);
  const m = hexToWorld(M.q, M.r);
  const e = hexToWorld(E.q, E.r);
  const rim = [-6600, 6600].flatMap((x) => [-3, 1, 4].map((r) => ({ x, y: r * 866 })));
  return {
    radius: R,
    rocks,
    base,
    maple: M,
    knot: E,
    strands: all,
    ...view,
    trees,
    start: { x: e.x + 1800, y: e.y + 600 },
    ceda: { x: m.x - 2400, y: m.y + 1500 },
    hob: { x: m.x + 2400, y: m.y + 1400 },
    rim,
  };
};

export const underLayout = (seed: number, pre: number): UnderLayout => {
  for (let k = 0; k < 600; k++) {
    const L = tryUnder(seedRng(deriveSeed(seed, 0x0d0e + k)), pre);
    if (L) return L;
  }
  throw new Error('underLayout: no net');
};
